import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useConfirmDialog } from '../components/ConfirmDialog';
import MediaInteractionModal from '../components/MediaInteractionModal';
import { connectSocket } from '../utils/socket';
import api, { chatApi } from '../utils/api';
import { getPublicPostShareUrl, getPublicProfileShareUrl, shareLink } from '../utils/share';
import {
  getUnseenOpenAuditionsCount,
  subscribeToOpenAuditionsViewed,
} from '../utils/openAuditions';
import './Content.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

const getMediaUrl = (key: string | null | undefined): string | null => {
  if (!key) return null;
  if (key.startsWith('http')) return key;
  return `${BACKEND_URL}/uploads/${key}`;
};

const refreshMediaUrl = async (mediaId: number): Promise<string | null> => {
  if (!Number.isFinite(mediaId) || mediaId <= 0) return null;
  const res = await api.get(`/media/item/${mediaId}`);
  return res.data?.media?.file_url || null;
};

const SEEN_STORAGE_KEY = 'paragone_pending_seen';

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

interface TopComment {
  id: number;
  comment_text: string;
  user_name: string;
  like_count: number;
}

interface FeedItem {
  id: number;
  user_id: number;
  media_type: 'photo' | 'video';
  file_name: string;
  file_url: string;
  file_srcset?: string | null;
  file_size?: number;
  caption: string | null;
  created_at: string;
  uploader_name: string;
  uploader_dp: string | null;
  uploader_dp_srcset?: string | null;
  uploader_roles: string[];
  uploader_city: string | null;
  uploader_state: string | null;
  like_count: number;
  comment_count: number;
  liked_by_me: boolean;
  seen_by_me: boolean;
  followed_by_me?: boolean;
  top_comment: TopComment | null;
}

const getFeedOrderBucket = (item: FeedItem, viewerId?: number) => {
  if (viewerId && item.user_id === viewerId && !item.seen_by_me) return 0;
  if (!item.seen_by_me && item.followed_by_me) return 1;
  if (!item.seen_by_me) return 2;
  return 3;
};

const sortFeedForViewer = (items: FeedItem[], viewerId?: number) =>
  items
    .filter((item) => !(viewerId && item.user_id === viewerId && item.seen_by_me))
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      const bucketDiff = getFeedOrderBucket(a.item, viewerId) - getFeedOrderBucket(b.item, viewerId);
      return bucketDiff || a.index - b.index;
    })
    .map(({ item }) => item);

interface Comment {
  id: number;
  comment_text: string;
  user_id: number;
  user_name: string;
  user_dp: string | null;
  like_count: number;
  liked_by_me: boolean;
  created_at: string;
}

interface Collaborator {
  id: number;
  media_id: number;
  role_name: string;
  user_id: number;
  user_name: string;
  user_dp: string | null;
  user_roles: string[];
  user_city: string | null;
  user_state: string | null;
}

interface Liker {
  id: number;
  name: string;
  profile_picture: string | null;
  liked_at: string;
}

interface UserProfile {
  user: {
    id: number;
    name: string;
    email: string;
    city?: string;
    state?: string;
    roles?: string[];
    profile_picture?: string;
    profile_completed?: boolean;
  };
  connectionStatus: string | null;
  connectionInitiatedBy: number | null;
  isFollowing: boolean;
  connectionCount?: number;
  followerCount?: number;
  photos: { id: number; file_name: string; file_url: string; media_type: string; caption?: string | null; like_count?: number; comment_count?: number; is_collab?: boolean; collaborator_role?: string | null; owner_name?: string | null }[];
  videos: { id: number; file_name: string; file_url: string; media_type: string; caption?: string | null; like_count?: number; comment_count?: number; is_collab?: boolean; collaborator_role?: string | null; owner_name?: string | null }[];
}

// ─────────────────────────────────────────────────────────────
// Comment Modal
// ─────────────────────────────────────────────────────────────

interface CommentModalProps {
  item: FeedItem;
  currentUserId: number;
  canInteract: boolean;
  onAuthRequired: () => void;
  onClose: () => void;
  onLikeUpdate: (mediaId: number, liked: boolean, likeCount: number) => void;
  onUploaderClick: (userId: number) => void;
}

const CommentModal: React.FC<CommentModalProps> = ({
  item,
  currentUserId,
  canInteract,
  onAuthRequired,
  onClose,
  onLikeUpdate,
  onUploaderClick,
}) => {
  const { confirm, notify, confirmDialog } = useConfirmDialog();
  const [comments, setComments]     = useState<Comment[]>([]);
  const [loaded, setLoaded]         = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [likeCount, setLikeCount]   = useState(item.like_count);
  const [likedByMe, setLikedByMe]   = useState(item.liked_by_me);
  const [showLikers, setShowLikers] = useState(false);
  const [likers, setLikers]         = useState<Liker[]>([]);
  const [likersLoading, setLikersLoading] = useState(false);
  const [src, setSrc] = useState(item.file_url);
  const [mediaLoading, setMediaLoading] = useState(true);
  const refreshingMediaRef = useRef(false);
  const commentsEndRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setSrc(item.file_url);
  }, [item.file_url]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    loadComments();
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    if (loaded) commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [comments.length, loaded]);

  const resizeComposer = useCallback(() => {
    const textarea = composerRef.current;
    if (!textarea) return;
    textarea.style.height = '0px';
    const nextHeight = Math.min(Math.max(textarea.scrollHeight, 48), 160);
    textarea.style.height = `${nextHeight}px`;
    textarea.style.overflowY = textarea.scrollHeight > 160 ? 'auto' : 'hidden';
  }, []);

  useEffect(() => {
    resizeComposer();
  }, [newComment, resizeComposer]);

  const loadComments = async () => {
    try {
      const res = await api.get(`/media/${item.id}/comments`);
      setComments(res.data.comments);
    } catch (err) {
      console.error('Load comments failed', err);
    } finally {
      setLoaded(true);
    }
  };

  const handleLike = async () => {
    if (!canInteract) {
      onAuthRequired();
      return;
    }
    try {
      const res = await api.post(`/media/${item.id}/like`);
      setLikedByMe(res.data.liked);
      setLikeCount(res.data.like_count);
      onLikeUpdate(item.id, res.data.liked, res.data.like_count);
    } catch (err) { console.error(err); }
  };

  const handleSubmit = async () => {
    if (!canInteract) {
      onAuthRequired();
      return;
    }
    if (!newComment.trim() || submitting) return;
    setSubmitting(true);
    try {
      const res = await api.post(`/media/${item.id}/comments`, { commentText: newComment.trim() });
      setComments(prev => [...prev, res.data.comment]);
      setNewComment('');
      setTimeout(() => commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (err) {
      console.error('Comment post error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    if (!canInteract) {
      onAuthRequired();
      return;
    }
    const approved = await confirm({
      title: 'Delete Comment',
      message: 'Delete this comment?',
      confirmLabel: 'Delete',
      cancelLabel: 'Keep',
      danger: true,
    });
    if (!approved) return;
    try {
      await api.delete(`/media/${item.id}/comments/${commentId}`);
      setComments(prev => prev.filter(c => c.id !== commentId));
    } catch (err) { console.error(err); }
  };

  const handleCommentLike = async (commentId: number) => {
    if (!canInteract) {
      onAuthRequired();
      return;
    }
    try {
      const res = await api.post(`/media/${item.id}/comments/${commentId}/like`);
      setComments(prev => prev.map(c =>
        c.id === commentId ? { ...c, liked_by_me: res.data.liked, like_count: res.data.like_count } : c,
      ));
    } catch (err) { console.error(err); }
  };

  const handleShowLikers = async () => {
    setShowLikers(true);
    setLikersLoading(true);
    try {
      const res = await api.get(`/media/${item.id}/likes`);
      setLikers(res.data.likers || []);
    } catch (err) {
      console.error('Load likers failed', err);
    } finally {
      setLikersLoading(false);
    }
  };

  const isOwner = item.user_id === currentUserId;
  const shareUrl = getPublicPostShareUrl(item.id);

  const handleShare = async () => {
    try {
      const outcome = await shareLink(shareUrl, `${item.uploader_name} on FilMini`);
      if (outcome === 'copied') {
        await notify({
          title: 'Link Copied',
          message: 'The share link has been copied to your clipboard.',
          confirmLabel: 'OK',
        });
      }
    } catch (error) {
      console.error('Share failed', error);
    }
  };

  const handleMediaError = async () => {
    if (refreshingMediaRef.current) return;
    refreshingMediaRef.current = true;
    try {
      const freshUrl = await refreshMediaUrl(item.id);
      if (freshUrl && freshUrl !== src) {
        setSrc(freshUrl);
      }
    } catch (error) {
      console.error('Refresh media URL failed', error);
    } finally {
      refreshingMediaRef.current = false;
    }
  };

  useEffect(() => { setMediaLoading(true); }, [src]);

  return (
    <>
      {confirmDialog}
      <div className="cf-modal-overlay" onClick={onClose}>
        <div className="cf-modal" onClick={e => e.stopPropagation()}>
        <button className="cf-modal-close" onClick={onClose}>✕</button>

        <div className="cf-modal-media">
          {mediaLoading && <div className="cf-modal-media-loading" aria-label="Loading media" />}
          {item.media_type === 'video' ? (
            <video
              className="cf-modal-media-el"
              controls autoPlay playsInline src={src}
              onError={handleMediaError}
              onContextMenu={e => e.preventDefault()}
              controlsList="nodownload"
              onCanPlay={() => setMediaLoading(false)}
            >
              <track kind="captions" src="/captions.vtt" srcLang="en" label="English captions" />
            </video>
          ) : (
            <img
              className="cf-modal-media-el"
              src={src} alt=""
              loading="lazy"
              onError={handleMediaError}
              onContextMenu={e => e.preventDefault()}
              draggable={false}
              onLoad={() => setMediaLoading(false)}
            />
          )}
        </div>

        <div className="cf-modal-info">
          <div
            className="cf-modal-uploader"
            style={{ cursor: 'pointer' }}
            onClick={() => { onUploaderClick(item.user_id); onClose(); }}
          >
            <div className="cf-modal-avatar">
              {item.uploader_dp
                ? <img src={item.uploader_dp} alt={item.uploader_name} />
                : <span>{item.uploader_name?.charAt(0).toUpperCase()}</span>
              }
            </div>
            <div>
              <p className="cf-modal-uname">{item.uploader_name}</p>
              {(item.uploader_city || item.uploader_state) && (
                <p className="cf-modal-uloc">
                  {[item.uploader_city, item.uploader_state].filter(Boolean).join(', ')}
                </p>
              )}
            </div>
          </div>

          {item.caption && <p className="cf-modal-caption">{item.caption}</p>}

          <div className="cf-modal-likebar">
            <button
              className={`cf-like-btn ${likedByMe ? 'cf-like-btn--active' : ''}`}
              onClick={handleLike}
              aria-label={likedByMe ? 'Unlike' : 'Like'}
            >
              <span className="cf-heart-icon">{likedByMe ? '\u2665' : '\u2661'}</span>
            </button>
            <button className="cf-like-count-btn" onClick={handleShowLikers}>
              {likeCount} like{likeCount === 1 ? '' : 's'}
            </button>
            <span className="cf-comment-count">
              {comments.length} comment{comments.length !== 1 ? 's' : ''}
            </span>
            <button className="cf-like-count-btn" onClick={handleShare}>
              <span aria-hidden="true">🔗</span> Share
            </button>
          </div>

          <div className="cf-modal-comments">
            {showLikers ? (
              likersLoading ? (
                <div className="cf-comments-loading">Loading...</div>
              ) : likers.length === 0 ? (
                <p className="cf-no-comments">No likes yet.</p>
              ) : (
                <div className="cf-likers-list">
                  {likers.map(liker => {
                    const avatar = getMediaUrl(liker.profile_picture);
                    return (
                      <button
                        key={liker.id}
                        className="cf-liker-row"
                        onClick={() => {
                          onUploaderClick(liker.id);
                          onClose();
                        }}
                      >
                        <div className="cf-liker-avatar">
                          {avatar ? (
                            <img src={avatar} alt={liker.name} />
                          ) : (
                            <span>{liker.name.charAt(0).toUpperCase()}</span>
                          )}
                        </div>
                        <span className="cf-liker-name">{liker.name}</span>
                      </button>
                    );
                  })}
                </div>
              )
            ) : !loaded ? (
              <div className="cf-comments-loading">Loading…</div>
            ) : comments.length === 0 ? (
              <p className="cf-no-comments">No comments yet. Be the first!</p>
            ) : comments.map(c => (
              <div key={c.id} className="cf-comment">
                <div className="cf-comment-avatar">
                  {c.user_dp
                    ? <img src={c.user_dp} alt={c.user_name} />
                    : <span>{c.user_name?.charAt(0).toUpperCase()}</span>
                  }
                </div>
                <div className="cf-comment-body">
                  <span className="cf-comment-author">{c.user_name}</span>
                  <span className="cf-comment-text">{c.comment_text}</span>
                </div>
                <div className="cf-comment-actions">
                  <button
                    className={`cf-clike-btn ${c.liked_by_me ? 'cf-clike-btn--active' : ''}`}
                    onClick={() => handleCommentLike(c.id)}
                  >
                    {c.liked_by_me ? '♥' : '♡'}{c.like_count > 0 ? ` ${c.like_count}` : ''}
                  </button>
                  {(isOwner || c.user_id === currentUserId) && (
                    <button className="cf-cdel-btn" onClick={() => handleDeleteComment(c.id)}>×</button>
                  )}
                </div>
              </div>
            ))}
            <div ref={commentsEndRef} />
          </div>

          <div className="cf-comment-input-row">
            <textarea
              ref={composerRef}
              className="cf-comment-input"
              placeholder={canInteract ? 'Add a comment…' : 'Log in to add a comment…'}
              value={newComment}
              onChange={e => setNewComment(e.target.value)}
              onInput={resizeComposer}
              onFocus={() => {
                if (!canInteract) {
                  onAuthRequired();
                }
              }}
              maxLength={500}
              rows={1}
              readOnly={!canInteract}
            />
            <button
              className="cf-comment-submit"
              onClick={canInteract ? handleSubmit : onAuthRequired}
              disabled={canInteract ? (!newComment.trim() || submitting) : false}
            >
              {canInteract ? (submitting ? '…' : 'Post') : 'Login'}
            </button>
          </div>
        </div>
        </div>
      </div>
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// MediaCard
// ─────────────────────────────────────────────────────────────

interface MediaCardProps {
  item: FeedItem;
  currentUserId: number;
  canInteract: boolean;
  onAuthRequired: () => void;
  onLikeUpdate: (mediaId: number, liked: boolean, likeCount: number) => void;
  onUploaderClick: (userId: number) => void;
  cardRef: (el: HTMLDivElement | null) => void;
  onMarkSeen: (mediaId: number) => void;
}

const MediaCard: React.FC<MediaCardProps> = ({
  item, currentUserId, canInteract, onAuthRequired, onLikeUpdate, onUploaderClick, cardRef, onMarkSeen,
}) => {
  const { notify, confirmDialog } = useConfirmDialog();
  const [showModal, setShowModal]         = useState(false);
  const [likeCount, setLikeCount]         = useState(item.like_count);
  const [likedByMe, setLikedByMe]         = useState(item.liked_by_me);
  const [commentCount]                    = useState(item.comment_count);
  const [collabs, setCollabs]             = useState<Collaborator[]>([]);
  const [collabLoaded, setCollabLoaded]   = useState(false);
  const [collabOpen, setCollabOpen]       = useState(false);
  const [collabLoading, setCollabLoading] = useState(false);
  const [src, setSrc]                     = useState(item.file_url);
  const shareUrl = getPublicPostShareUrl(item.id);

  const videoRef = useRef<HTMLVideoElement>(null);
  const refreshingMediaRef = useRef(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) video.play().catch(() => {});
        else video.pause();
      },
      { threshold: 0.7 },
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setLikeCount(item.like_count);
    setLikedByMe(item.liked_by_me);
  }, [item.like_count, item.liked_by_me]);

  useEffect(() => {
    setSrc(item.file_url);
  }, [item.file_url]);

  const handleOpenModal = () => {
    onMarkSeen(item.id);
    setShowModal(true);
  };

  const handleLike = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!canInteract) {
      onAuthRequired();
      return;
    }
    onMarkSeen(item.id);
    try {
      const res = await api.post(`/media/${item.id}/like`);
      setLikedByMe(res.data.liked);
      setLikeCount(res.data.like_count);
      onLikeUpdate(item.id, res.data.liked, res.data.like_count);
    } catch (err) { console.error(err); }
  };

  const handleLikeUpdate = (mediaId: number, liked: boolean, count: number) => {
    setLikedByMe(liked);
    setLikeCount(count);
    onLikeUpdate(mediaId, liked, count);
  };

  const handleModalClose = () => {
    setShowModal(false);
  };

  const toggleCollabs = async () => {
    const next = !collabOpen;
    setCollabOpen(next);
    if (next && !collabLoaded) {
      setCollabLoading(true);
      try {
        const res = await api.get(`/media/${item.id}/collaborators`);
        setCollabs(res.data.collaborators);
        setCollabLoaded(true);
      } catch (err) { console.error(err); }
      finally { setCollabLoading(false); }
    }
  };

  const handleShare = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const outcome = await shareLink(shareUrl, `${item.uploader_name} on FilMini`);
      if (outcome === 'copied') {
        await notify({
          title: 'Link Copied',
          message: 'The share link has been copied to your clipboard.',
          confirmLabel: 'OK',
        });
      }
    } catch (error) {
      console.error('Share failed', error);
    }
  };

  const handleMediaError = async () => {
    if (refreshingMediaRef.current) return;
    refreshingMediaRef.current = true;
    try {
      const freshUrl = await refreshMediaUrl(item.id);
      if (freshUrl && freshUrl !== src) {
        setSrc(freshUrl);
      }
    } catch (error) {
      console.error('Refresh media URL failed', error);
    } finally {
      refreshingMediaRef.current = false;
    }
  };

  return (
    <>
      {confirmDialog}
      {showModal && (
        <CommentModal
          item={{ ...item, file_url: src, like_count: likeCount, liked_by_me: likedByMe }}
          currentUserId={currentUserId}
          canInteract={canInteract}
          onAuthRequired={onAuthRequired}
          onClose={handleModalClose}
          onLikeUpdate={handleLikeUpdate}
          onUploaderClick={onUploaderClick}
        />
      )}

      <div className="cf-card" data-media-id={item.id} ref={cardRef}>
        {item.seen_by_me && <span className="cp-seen-badge">Seen</span>}

        <div className="cf-card-header" onClick={() => onUploaderClick(item.user_id)} style={{ cursor: 'pointer' }}>
          <div className="cf-card-avatar">
            {item.uploader_dp
              ? <img src={item.uploader_dp} srcSet={item.uploader_dp_srcset || undefined} sizes="40px" alt={item.uploader_name} width="40" height="40" />
              : <span>{item.uploader_name?.charAt(0).toUpperCase()}</span>
            }
          </div>
          <div className="cf-card-uploader-info">
            <p className="cf-card-uname">{item.uploader_name}</p>
            {(item.uploader_city || item.uploader_state) && (
              <p className="cf-card-uloc">
                {[item.uploader_city, item.uploader_state].filter(Boolean).join(', ')}
              </p>
            )}
            {item.uploader_roles?.length > 0 && (
              <p className="cf-card-uroles">{item.uploader_roles.slice(0, 2).join(' · ')}</p>
            )}
          </div>
        </div>

        <div
          className={`cf-card-media ${item.media_type === 'video' ? 'cf-card-media--video' : ''}`}
          onClick={handleOpenModal}
        >
          {item.media_type === 'video' ? (
            <>
              <video
                ref={videoRef}
                src={src}
                preload="metadata"
                muted playsInline loop
                onError={handleMediaError}
                onContextMenu={e => e.preventDefault()}
                style={{
                  width: '100%', height: '100%',
                  objectFit: 'contain', objectPosition: 'center',
                  display: 'block', background: '#181512',
                  pointerEvents: 'none',
                }}
              />
              <div className="cf-card-play">▶</div>
            </>
          ) : (
            <img
              src={src} alt=""
              srcSet={item.file_srcset || undefined}
              sizes="(max-width: 700px) 100vw, 680px"
              onError={handleMediaError}
              onContextMenu={e => e.preventDefault()}
              draggable={false}
              style={{ width: '100%', height: '100%', objectFit: 'contain', objectPosition: 'center', display: 'block' }}
            />
          )}
        </div>

        {item.caption && <p className="cf-card-caption">{item.caption}</p>}

        <div className="cf-card-bar">
          <button
            className={`cf-card-like-btn ${likedByMe ? 'cf-card-like-btn--active' : ''}`}
            onClick={handleLike}
          >
            <span className="cf-heart-icon">{likedByMe ? '\u2665' : '\u2661'}</span> {likeCount}
          </button>
          <button className="cf-card-comment-btn" onClick={handleOpenModal}>
            💬 {commentCount}
          </button>
          <button className="cf-card-comment-btn" onClick={handleShare}>
            <span aria-hidden="true">🔗</span> Share
          </button>
        </div>

        <div className="cf-card-collabs">
          <button className="cf-collabs-toggle" onClick={toggleCollabs}>
            <span>Collaborators</span>
            <span className="cf-collabs-arrow">{collabOpen ? '▲' : '▼'}</span>
          </button>
          {collabOpen && (
            <div className="cf-collabs-body">
              {collabLoading ? (
                <span className="cf-collabs-empty">Loading…</span>
              ) : collabs.length === 0 ? (
                <span className="cf-collabs-empty">No collaborators tagged</span>
              ) : (
                <div className="cp-collabs-list">
                  {collabs.map(c => {
                    const cdp = getMediaUrl(c.user_dp);
                    return (
                      <div key={c.id} className="cp-collab-chip" onClick={() => onUploaderClick(c.user_id)} style={{ cursor: 'pointer' }}>
                        <div className="cp-collab-avatar">
                          {cdp ? <img src={cdp} alt={c.user_name} /> : c.user_name.charAt(0).toUpperCase()}
                        </div>
                        <div className="cp-collab-info">
                          <span className="cp-collab-name">{c.user_name}</span>
                          <span className="cp-collab-role">{c.role_name}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {item.top_comment && (
          <div className="cf-card-top-comment" onClick={handleOpenModal}>
            <span className="cf-card-tc-author">{item.top_comment.user_name}</span>
            <span className="cf-card-tc-text">{item.top_comment.comment_text}</span>
            {commentCount > 1 && (
              <span className="cf-card-tc-more">View all {commentCount} comments</span>
            )}
          </div>
        )}
        {!item.top_comment && commentCount === 0 && (
          <button className="cf-card-add-comment" onClick={handleOpenModal}>
            Add a comment…
          </button>
        )}
      </div>
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// Main ContentPage
// ─────────────────────────────────────────────────────────────

const ContentPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAuthenticated } = useAuth();
  const isGuest = !isAuthenticated;

  const [feed, setFeed]               = useState<FeedItem[]>([]);
  const [searchQuery, setSearchQuery]   = useState('');
  const [loading, setLoading]         = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError]             = useState('');
  const [allSeen, setAllSeen]         = useState(false);
  const [menuOpen, setMenuOpen]       = useState(false);

  const offsetRef = useRef(0);
  const PAGE_SIZE = 10;

  const pendingSeenRef = useRef<Set<number>>(new Set());
  const seenTimersRef  = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());
  const observerRef    = useRef<IntersectionObserver | null>(null);
  const cardRefs       = useRef<Map<number, HTMLDivElement>>(new Map());
  const seedRef        = useRef<string>('');

  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);

  const filteredFeed = useMemo<FeedItem[]>(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return feed;
    return feed.filter((item) => {
      return [
        item.uploader_name,
        item.uploader_city,
        item.uploader_state,
        ...(item.uploader_roles || []),
        item.caption,
        item.top_comment?.comment_text,
        item.top_comment?.user_name,
      ]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(query));
    });
  }, [feed, searchQuery]);

  // FIX: store only pending counts, not all statuses
  const [receivedRequestsCount, setReceivedRequestsCount]     = useState(0);
  const [connectionRequestsCount, setConnectionRequestsCount] = useState(0);
  const [unreadDMCount, setUnreadDMCount]                     = useState(0);
  const [totalGroupUnread, setTotalGroupUnread]               = useState(0);
  const [openAuditionsCount, setOpenAuditionsCount]           = useState(0);
  const [calendarCount, setCalendarCount]                     = useState(0);

  const feedRef = useRef<HTMLDivElement>(null);
  const [sharedProfileMediaId, setSharedProfileMediaId] = useState<number | null>(null);
  const requireLogin = useCallback(() => {
    navigate('/login');
  }, [navigate]);

  useEffect(() => {
    if (isGuest) {
      setReceivedRequestsCount(0);
      setConnectionRequestsCount(0);
      setUnreadDMCount(0);
      setTotalGroupUnread(0);
      setOpenAuditionsCount(0);
      setCalendarCount(0);
      return;
    }

    // Group/role requests — only count pending ones
    api.get('/requests/received')
      .then(res => {
        const pending = (res.data.requests || []).filter(
          (r: any) => r.status === 'pending'
        );
        setReceivedRequestsCount(pending.length);
      })
      .catch(() => {});

    // Connection requests — only count pending ones
    api.get('/connections/requests')
      .then(res => {
        const pending = (res.data.requests || []).filter(
          (r: any) => r.status === 'pending'
        );
        setConnectionRequestsCount(pending.length);
      })
      .catch(() => {});

    chatApi.get('/messages/unread-count')
      .then(async (res) => {
        try {
          const customGroupsRes = await chatApi.get('/custom-groups/unread-counts');
          const customCounts: Record<number, number> = customGroupsRes.data.counts || {};
          const customUnread = Object.values(customCounts).reduce((sum, n) => sum + Number(n || 0), 0);
          setUnreadDMCount(Number(res.data.unread_count || 0) + customUnread);
        } catch {
          setUnreadDMCount(res.data.unread_count || 0);
        }
      })
      .catch(() => {});

    chatApi.get('/chat/unread-counts')
      .then(res => {
        const counts: Record<number, number> = res.data.counts || {};
        setTotalGroupUnread(Object.values(counts).reduce((sum, n) => sum + n, 0));
      })
      .catch(() => {});

    api.get('/public-auditions?includeMedia=false')
      .then(res => {
        setOpenAuditionsCount(getUnseenOpenAuditionsCount(res.data.auditions || []));
      })
      .catch(() => {});

    api.get('/schedules/calendar/unread-count')
      .then(res => {
        setCalendarCount(Number(res.data?.unreadCount || 0));
      })
      .catch(() => {});
  }, [isGuest]);

  useEffect(() => {
    if (isGuest) return;
    const timer = window.setInterval(() => {
      api.get('/schedules/calendar/unread-count')
        .then(res => {
          setCalendarCount(Number(res.data?.unreadCount || 0));
        })
        .catch(() => {});
    }, 20000);

    return () => window.clearInterval(timer);
  }, [isGuest]);

  useEffect(() => {
    if (isGuest) return;
    return subscribeToOpenAuditionsViewed(() => {
      setOpenAuditionsCount(0);
    });
  }, [isGuest]);

  useEffect(() => {
    if (!user?.id) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    const socket = connectSocket(token);
    const handleNewDm = (message: any) => {
      if (message.receiver_id === user.id) setUnreadDMCount(prev => prev + 1);
    };
    const handleNewGroupMessage = (message: any) => {
      if (message.user_id !== user.id) setTotalGroupUnread(prev => prev + 1);
    };
    const handleNewCustomGroupMessage = (message: any) => {
      if (message.user_id !== user.id) setUnreadDMCount(prev => prev + 1);
    };
    socket.on('new-direct-message', handleNewDm);
    socket.on('new-message', handleNewGroupMessage);
    socket.on('new-custom-group-message', handleNewCustomGroupMessage);
    return () => {
      socket.off('new-direct-message', handleNewDm);
      socket.off('new-message', handleNewGroupMessage);
      socket.off('new-custom-group-message', handleNewCustomGroupMessage);
    };
  }, [user?.id]);

  const savePendingSeen = useCallback(() => {
    if (isGuest) return;
    const ids = Array.from(pendingSeenRef.current);
    if (ids.length === 0) return;
    try {
      const existing: number[] = JSON.parse(sessionStorage.getItem(SEEN_STORAGE_KEY) || '[]');
      const merged = Array.from(new Set([...existing, ...ids]));
      sessionStorage.setItem(SEEN_STORAGE_KEY, JSON.stringify(merged));
      pendingSeenRef.current.clear();
    } catch { /* unavailable */ }
  }, [isGuest]);

  const flushStoredSeen = useCallback(async () => {
    if (isGuest) return;
    try {
      const raw = sessionStorage.getItem(SEEN_STORAGE_KEY);
      if (!raw) return;
      const ids: number[] = JSON.parse(raw);
      if (ids.length === 0) return;
      sessionStorage.removeItem(SEEN_STORAGE_KEY);
      await api.post('/media/seen/batch', { mediaIds: ids });
    } catch { /* non-critical */ }
  }, [isGuest]);

  useEffect(() => {
    window.addEventListener('beforeunload', savePendingSeen);
    return () => {
      window.removeEventListener('beforeunload', savePendingSeen);
      savePendingSeen();
    };
  }, [savePendingSeen]);

  const loadFeed = useCallback(async () => {
    setLoading(true);
    setError('');
    setAllSeen(false);
    offsetRef.current = 0;
    if (!seedRef.current) {
      seedRef.current = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }
    if (!isGuest) {
      await flushStoredSeen();
    }
    seenTimersRef.current.forEach(t => clearTimeout(t));
    seenTimersRef.current.clear();
    try {
      const res = await api.get(`/media/feed/list?offset=0&limit=${PAGE_SIZE}&seed=${encodeURIComponent(seedRef.current)}`);
      const loadedFeed: FeedItem[] = res.data.feed || [];
      setFeed(sortFeedForViewer(loadedFeed, user?.id));
      offsetRef.current = loadedFeed.length;
      setAllSeen(loadedFeed.length < PAGE_SIZE);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load content');
    } finally {
      setLoading(false);
    }
  }, [flushStoredSeen, isGuest, user?.id]);

  useEffect(() => { loadFeed(); }, [loadFeed]);

  useEffect(() => {
    if (isGuest) return;
    if (loading) return;
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          const mediaId = parseInt((entry.target as HTMLElement).dataset.mediaId || '0');
          if (!mediaId) return;
          if (entry.isIntersecting) {
            if (pendingSeenRef.current.has(mediaId)) return;
            if (seenTimersRef.current.has(mediaId)) return;
            const timer = setTimeout(() => {
              seenTimersRef.current.delete(mediaId);
              pendingSeenRef.current.add(mediaId);
            }, 2000);
            seenTimersRef.current.set(mediaId, timer);
          } else {
            const timer = seenTimersRef.current.get(mediaId);
            if (timer) { clearTimeout(timer); seenTimersRef.current.delete(mediaId); }
          }
        });
      },
      { threshold: 0.5 },
    );
    cardRefs.current.forEach(el => observerRef.current?.observe(el));
    return () => {
      observerRef.current?.disconnect();
      seenTimersRef.current.forEach(t => clearTimeout(t));
      seenTimersRef.current.clear();
    };
  }, [isGuest, loading]);

  useEffect(() => {
    if (isGuest) return;
    if (!observerRef.current) return;
    cardRefs.current.forEach(el => observerRef.current?.observe(el));
  }, [feed.length, isGuest]);

  const handleMarkSeen = useCallback((mediaId: number) => {
    const timer = seenTimersRef.current.get(mediaId);
    if (timer) {
      clearTimeout(timer);
      seenTimersRef.current.delete(mediaId);
    }
    pendingSeenRef.current.add(mediaId);
  }, []);

  const loadMore = useCallback(async () => {
    if (loadingMore || allSeen) return;
    setLoadingMore(true);
    try {
      const res = await api.get(
        `/media/feed/list?offset=${offsetRef.current}&limit=${PAGE_SIZE}&seed=${encodeURIComponent(seedRef.current)}`,
      );
      const newItems: FeedItem[] = sortFeedForViewer(res.data.feed || [], user?.id);
      if (newItems.length > 0) {
        setFeed(prev => [...prev, ...newItems]);
        offsetRef.current += newItems.length;
        if (newItems.length < PAGE_SIZE) setAllSeen(true);
      } else {
        setAllSeen(true);
      }
    } catch { /* ignore */ }
    finally { setLoadingMore(false); }
  }, [loadingMore, allSeen, user?.id]);

  const sentinelRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node) return;
      const io = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) loadMore();
      });
      io.observe(node);
    },
    [loadMore],
  );

  // A query searches the complete paginated feed, not just the first page.
  // Continue fetching until a match appears or the API confirms the end.
  useEffect(() => {
    if (searchQuery.trim() && filteredFeed.length === 0 && !loading && !loadingMore && !allSeen) {
      void loadMore();
    }
  }, [allSeen, filteredFeed.length, loadMore, loading, loadingMore, searchQuery]);

  const scrollToTop = () => {
    feedRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLikeUpdate = (mediaId: number, liked: boolean, likeCount: number) => {
    setFeed(prev =>
      prev.map(item => item.id === mediaId ? { ...item, liked_by_me: liked, like_count: likeCount } : item),
    );
  };

  const handleLogout = async () => {
    savePendingSeen();
    await logout();
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sharedUserIdRaw = params.get('sharedUserId');
    const mediaIdRaw = params.get('mediaId');

    if (!sharedUserIdRaw) {
      return;
    }

    const sharedUserId = Number(sharedUserIdRaw);
    if (!Number.isFinite(sharedUserId) || sharedUserId <= 0) {
      return;
    }

    const mediaId = mediaIdRaw ? Number(mediaIdRaw) : null;
    if (mediaIdRaw && (!Number.isFinite(mediaId) || (mediaId ?? 0) <= 0)) {
      return;
    }

    setSelectedUserId(sharedUserId);
    setSharedProfileMediaId(mediaId ?? null);
    navigate('/content', { replace: true });
  }, [location.search, navigate]);

  return (
    <div className="cp-container">

      {/* Header */}
      <div className="cp-header">
        <span className="cp-title">{isGuest ? 'Welcome User' : `Welcome, ${user?.name || 'User'}!`}</span>

        <div className="cp-header-actions">
          <button onClick={() => isGuest ? requireLogin() : navigate('/user-search')} className="find-people-btn">Find People</button>
          <span className="btn-badge-wrap">
            <button onClick={() => isGuest ? requireLogin() : navigate('/open-auditions')} className="open-auditions-btn">Auditions</button>
            {!isGuest && openAuditionsCount > 0 && <span className="notification-badge">{openAuditionsCount}</span>}
          </span>
          <span className="btn-badge-wrap">
            <button onClick={() => isGuest ? requireLogin() : navigate('/home')} className="content-btn">Projects</button>
            {!isGuest && totalGroupUnread > 0 && <span className="notification-badge">{totalGroupUnread}</span>}
          </span>
          <span className="btn-badge-wrap">
            <button onClick={() => isGuest ? requireLogin() : navigate('/calendar')} className="content-btn">Calendar</button>
            {!isGuest && calendarCount > 0 && <span className="notification-badge">{calendarCount}</span>}
          </span>
          <span className="btn-badge-wrap">
            <button onClick={() => isGuest ? requireLogin() : navigate('/direct-messages')} className="chat-btn-home">Chats</button>
            {!isGuest && unreadDMCount > 0 && <span className="notification-badge">{unreadDMCount}</span>}
          </span>
          <span className="btn-badge-wrap">
            <button onClick={() => isGuest ? requireLogin() : navigate('/requests')} className="requests-btn">Requests</button>
            {!isGuest && (receivedRequestsCount + connectionRequestsCount) > 0 && (
              <span className="notification-badge">{receivedRequestsCount + connectionRequestsCount}</span>
            )}
          </span>
          <button onClick={() => isGuest ? requireLogin() : navigate('/profile')} className="profile-btn">
            {isGuest ? 'Login' : 'View Profile'}
          </button>
          {!isGuest && <button onClick={handleLogout} className="logout-btn">Logout</button>}
        </div>

        <span className="btn-badge-wrap">
  <button
    className={`hamburger-btn${menuOpen ? ' is-open' : ''}`}
    onClick={() => setMenuOpen(prev => !prev)}
    aria-label="Menu"
  >
    <span /><span /><span />
  </button>
  {!isGuest && (unreadDMCount + receivedRequestsCount + connectionRequestsCount + totalGroupUnread + openAuditionsCount + calendarCount) > 0 && (
    <span className="notification-badge">
      {unreadDMCount + receivedRequestsCount + connectionRequestsCount + totalGroupUnread + openAuditionsCount + calendarCount}
    </span>
  )}
</span>
      </div>

      {!menuOpen && (
        <div className="cp-search-bar">
          <input
            type="search"
            placeholder="Search creators, roles, locations, and captions"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="cp-search-input"
            aria-label="Search content"
          />
          {searchQuery && (
            <button type="button" className="cp-search-clear" onClick={() => setSearchQuery('')}>
              Clear
            </button>
          )}
        </div>
      )}

      {/* Mobile drawer */}
      <div className={`mobile-nav${menuOpen ? ' is-open' : ''}`}>
        <button onClick={() => { isGuest ? requireLogin() : navigate('/user-search'); setMenuOpen(false); }} className="find-people-btn">Find People</button>
        <span className="btn-badge-wrap">
          <button onClick={() => { isGuest ? requireLogin() : navigate('/open-auditions'); setMenuOpen(false); }} className="open-auditions-btn">Auditions</button>
          {!isGuest && openAuditionsCount > 0 && <span className="notification-badge">{openAuditionsCount}</span>}
        </span>
        <span className="btn-badge-wrap">
          <button onClick={() => { isGuest ? requireLogin() : navigate('/home'); setMenuOpen(false); }} className="content-btn">Projects</button>
          {!isGuest && totalGroupUnread > 0 && <span className="notification-badge">{totalGroupUnread}</span>}
        </span>
        <span className="btn-badge-wrap">
          <button onClick={() => { isGuest ? requireLogin() : navigate('/calendar'); setMenuOpen(false); }} className="content-btn">Calendar</button>
          {!isGuest && calendarCount > 0 && <span className="notification-badge">{calendarCount}</span>}
        </span>
        <span className="btn-badge-wrap">
          <button onClick={() => { isGuest ? requireLogin() : navigate('/direct-messages'); setMenuOpen(false); }} className="chat-btn-home">Chats</button>
          {!isGuest && unreadDMCount > 0 && <span className="notification-badge">{unreadDMCount}</span>}
        </span>
        <span className="btn-badge-wrap">
          <button onClick={() => { isGuest ? requireLogin() : navigate('/requests'); setMenuOpen(false); }} className="requests-btn">Requests</button>
          {!isGuest && (receivedRequestsCount + connectionRequestsCount) > 0 && (
            <span className="notification-badge">{receivedRequestsCount + connectionRequestsCount}</span>
          )}
        </span>
        <button onClick={() => { isGuest ? requireLogin() : navigate('/profile'); setMenuOpen(false); }} className="profile-btn">
          {isGuest ? 'Login' : 'View Profile'}
        </button>
        {!isGuest && <button onClick={handleLogout} className="logout-btn">Logout</button>}
      </div>

      {/* Feed */}
      {loading && <div className="cp-loading">Loading content...</div>}
      {!loading && error && <div className="cp-error">{error}</div>}
      {!loading && !error && feed.length === 0 && (
        <div className="cp-empty">
          <p>No content yet. Connect with people and explore their work here.</p>
        </div>
      )}

      {!loading && !error && feed.length > 0 && filteredFeed.length === 0 && searchQuery && allSeen && (
        <div className="cp-empty">
          <p>No results found for “{searchQuery}”. Try another search term or clear the filter.</p>
        </div>
      )}

      {!loading && !error && filteredFeed.length > 0 && (
        <div className="cp-scroll-feed" ref={feedRef}>
          {filteredFeed.map(item => (
            <MediaCard
              key={item.id}
              item={item}
              currentUserId={user?.id ?? 0}
              canInteract={!isGuest}
              onAuthRequired={requireLogin}
              onLikeUpdate={handleLikeUpdate}
              onUploaderClick={(userId) => {
                setSelectedUserId(userId);
                setSharedProfileMediaId(null);
              }}
              onMarkSeen={isGuest ? () => {} : handleMarkSeen}
              cardRef={el => {
                if (el) {
                  cardRefs.current.set(item.id, el);
                  observerRef.current?.observe(el);
                } else {
                  cardRefs.current.delete(item.id);
                }
              }}
            />
          ))}

          {loadingMore && (
            <div className="cp-sentinel">
              <div className="cp-counter-loading">Loading more…</div>
            </div>
          )}

          {!allSeen && !loadingMore && (
            <div ref={sentinelRef} style={{ height: 1 }} />
          )}

          {allSeen && (
            <div className="cp-all-seen-banner">
              <p className="cp-all-seen-text">✦ You've reached the end</p>
              <button className="cp-scroll-top-btn" onClick={scrollToTop}>
                Back to top
              </button>
            </div>
          )}
        </div>
      )}

      <button
        className="cp-fab"
        onClick={() => isGuest ? requireLogin() : navigate('/profile')}
        aria-label="Add media"
        title="Add to your portfolio"
      >
        +
      </button>

      {selectedUserId && (
        <ContentProfileModal
          userId={selectedUserId}
          initialMediaId={sharedProfileMediaId}
          canInteract={!isGuest}
          onAuthRequired={requireLogin}
          onClose={() => {
            setSelectedUserId(null);
            setSharedProfileMediaId(null);
          }}
        />
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Profile Modal
// ─────────────────────────────────────────────────────────────

interface ContentProfileModalProps {
  userId: number;
  onClose: () => void;
  initialMediaId?: number | null;
  canInteract?: boolean;
  onAuthRequired?: () => void;
}

export const ContentProfileModal: React.FC<ContentProfileModalProps> = ({
  userId,
  onClose,
  initialMediaId = null,
  canInteract = true,
  onAuthRequired,
}) => {
  const navigate = useNavigate();
  const { notify } = useConfirmDialog();
  const [profile, setProfile]             = useState<UserProfile | null>(null);
  const [loading, setLoading]             = useState(true);
  const [viewMediaType, setViewMediaType] = useState<'photos' | 'videos'>('photos');
  const [dpError, setDpError]             = useState(false);
  const [connecting, setConnecting]       = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [followUpdating, setFollowUpdating] = useState(false);
  const [connectMsg, setConnectMsg]       = useState('');
  const [lightbox, setLightbox]           = useState<{ url: string; isVideo: boolean; mediaId: number } | null>(null);
  const [collabMap, setCollabMap]         = useState<Record<number, Collaborator[]>>({});
  const [collabLoading, setCollabLoading] = useState<Record<number, boolean>>({});
  const [initialMediaOpened, setInitialMediaOpened] = useState(false);
  const refreshingProfileMediaRef = useRef<Set<number>>(new Set());


  useEffect(() => {
    setLoading(true);
    setDpError(false);
    setCollabMap({});
    setInitialMediaOpened(false);
    api.get(`/user/${userId}/profile`)
      .then(res => setProfile(res.data))
      .catch(err => console.error('Failed to load profile:', err))
      .finally(() => setLoading(false));
  }, [userId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { if (lightbox) setLightbox(null); else onClose(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox, onClose]);

  const fetchCollabs = useCallback(async (mediaId: number) => {
    if (collabMap[mediaId] !== undefined) return;
    setCollabLoading(prev => ({ ...prev, [mediaId]: true }));
    try {
      const res = await api.get(`/media/${mediaId}/collaborators`);
      setCollabMap(prev => ({ ...prev, [mediaId]: res.data.collaborators }));
    } catch {
      setCollabMap(prev => ({ ...prev, [mediaId]: [] }));
    } finally {
      setCollabLoading(prev => ({ ...prev, [mediaId]: false }));
    }
  }, [collabMap]);

  const openLightbox = (url: string, isVideo: boolean, mediaId: number) => {
    setLightbox({ url, isVideo, mediaId });
    fetchCollabs(mediaId);
  };

  const updateProfileMediaUrl = useCallback((mediaId: number, freshUrl: string) => {
    setProfile((current) => {
      if (!current) return current;
      const updateList = (items: UserProfile['photos']) =>
        items.map((item) => (item.id === mediaId ? { ...item, file_url: freshUrl } : item));
      return {
        ...current,
        photos: updateList(current.photos || []),
        videos: updateList(current.videos || []),
      };
    });
  }, []);

  const refreshProfileMediaUrl = useCallback(async (mediaId: number) => {
    if (refreshingProfileMediaRef.current.has(mediaId)) return null;
    refreshingProfileMediaRef.current.add(mediaId);
    try {
      const freshUrl = await refreshMediaUrl(mediaId);
      if (freshUrl) {
        updateProfileMediaUrl(mediaId, freshUrl);
        setLightbox((current) => (current?.mediaId === mediaId ? { ...current, url: freshUrl } : current));
      }
      return freshUrl;
    } catch (error) {
      console.error('Refresh profile media URL failed', error);
      return null;
    } finally {
      refreshingProfileMediaRef.current.delete(mediaId);
    }
  }, [updateProfileMediaUrl]);

  useEffect(() => {
    if (!profile || !initialMediaId || initialMediaOpened) return;

    const target = [...(profile.photos || []), ...(profile.videos || [])].find((item) => item.id === initialMediaId);
    if (!target) return;

    const url = target.file_url || getMediaUrl(target.file_name);
    if (!url) return;

    setInitialMediaOpened(true);
    openLightbox(url, target.media_type === 'video', target.id);
  }, [initialMediaId, initialMediaOpened, profile]);

  const handleConnect = async () => {
    if (profile && profile.user.profile_completed === false) {
      setConnectMsg('Receiver profile yet to be completed');
      return;
    }
    setConnecting(true);
    try {
      await api.post('/connections/request', { targetUserId: userId });
      setConnectMsg('Connection request sent!');
      if (profile) setProfile({ ...profile, connectionStatus: 'pending' });
    } catch (err: any) {
      setConnectMsg(err.response?.data?.message || 'Failed to send request');
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    setConnectMsg('');
    setDisconnecting(true);
    try {
      await api.delete(`/connections/${userId}`);
      setConnectMsg('Disconnected successfully!');
      setProfile((current) =>
        current
          ? {
              ...current,
              connectionStatus: null,
              connectionInitiatedBy: null,
            }
          : current,
      );
    } catch (err: any) {
      setConnectMsg(err.response?.data?.message || 'Failed to disconnect');
    } finally {
      setDisconnecting(false);
    }
  };

  const handleFollowToggle = async () => {
    if (!canInteract) {
      onAuthRequired?.();
      return;
    }

    setConnectMsg('');
    setFollowUpdating(true);
    try {
      if (profile?.isFollowing) {
        const response = await api.delete(`/users/${userId}/follow`);
        setConnectMsg('Unfollowed successfully!');
        setProfile((current) => (current ? { ...current, isFollowing: Boolean(response.data?.isFollowing) } : current));
      } else {
        const response = await api.post(`/users/${userId}/follow`);
        setConnectMsg('Following successfully!');
        setProfile((current) => (current ? { ...current, isFollowing: Boolean(response.data?.isFollowing) } : current));
      }
    } catch (err: any) {
      setConnectMsg(err.response?.data?.message || 'Failed to update follow');
    } finally {
      setFollowUpdating(false);
    }
  };

  if (loading || !profile) {
    return (
      <div className="cp-overlay" onClick={onClose}>
        <div className="cp-modal" onClick={e => e.stopPropagation()}>
          <div className="cp-modal-loading">Loading…</div>
        </div>
      </div>
    );
  }

  const dpUrl      = getMediaUrl(profile.user.profile_picture);
  const photos     = profile.photos || [];
  const videos     = profile.videos || [];
  const activeMedia = viewMediaType === 'photos' ? photos : videos;

  return (
    <>
      <div className="cp-overlay" onClick={onClose}>
        <div className="cp-modal" onClick={e => e.stopPropagation()}>
          <button onClick={onClose} className="cp-modal-close">✕</button>

          <div className="cp-modal-header">
            <div
              className={`cp-modal-avatar${dpUrl && !dpError ? ' clickable' : ''}`}
              onClick={() => {
                if (!dpUrl || dpError) return;
                openLightbox(dpUrl, false, -1);
              }}
            >
              {dpUrl && !dpError
                ? <img src={dpUrl} alt={profile.user.name} onError={() => setDpError(true)} />
                : profile.user.name.charAt(0).toUpperCase()
              }
            </div>
            <h2>{profile.user.name}</h2>
            <p className="cp-modal-email">{profile.user.email}</p>
            <div className="cp-profile-stats">
              <span><strong>{profile.connectionCount ?? 0}</strong> Connections</span>
              <span><strong>{profile.followerCount ?? 0}</strong> Followers</span>
            </div>
            <button type="button" className="cp-modal-share-btn" onClick={async () => {
              try {
                const outcome = await shareLink(getPublicProfileShareUrl(userId), `${profile.user.name} on FilMini`);
                if (outcome === 'copied') {
                  notify({ title: 'Link Copied', message: 'Profile link copied to clipboard.', confirmLabel: 'OK' });
                }
              } catch (err) {
                console.error('Profile share failed', err);
              }
            }}>
              Share Profile
            </button>
          </div>

          <div className="cp-modal-details">
            {profile.user.roles && profile.user.roles.length > 0 && (
              <div className="cp-modal-detail-row">
                <span className="cp-modal-detail-label">Roles</span>
                <div className="cp-modal-detail-value cp-modal-roles">
                  {profile.user.roles.map((role, i) => <span key={i} className="cp-role-tag">{role}</span>)}
                </div>
              </div>
            )}
            {profile.user.city && (
              <div className="cp-modal-detail-row">
                <span className="cp-modal-detail-label">Location</span>
                <span className="cp-modal-detail-value">{profile.user.city}, {profile.user.state}</span>
              </div>
            )}
          </div>

          {(photos.length > 0 || videos.length > 0) && (
            <div className="cp-modal-gallery">
              <div className="cp-modal-tabs">
                <button className={`cp-modal-tab ${viewMediaType === 'photos' ? 'active' : ''}`} onClick={() => setViewMediaType('photos')}>
                  Photos ({photos.length})
                </button>
                <button className={`cp-modal-tab ${viewMediaType === 'videos' ? 'active' : ''}`} onClick={() => setViewMediaType('videos')}>
                  Videos ({videos.length})
                </button>
              </div>
              {activeMedia.length > 0 ? (
                <div className="cp-modal-media-grid">
                  {activeMedia.map(item => {
                    const url = item.file_url || getMediaUrl(item.file_name);
                    if (!url) return null;
                    const isVideo = item.media_type === 'video';
                    return (
                      <div
                        key={item.id}
                        className={`cp-modal-media-item${isVideo ? ' cp-modal-media-item--video' : ''}`}
                        onClick={() => {
                          openLightbox(url, isVideo, item.id);
                        }}
                      >
                        {isVideo ? (
                          <>
                            <video src={`${url}#t=0.5`} preload="metadata" muted playsInline
                              onError={() => { refreshProfileMediaUrl(item.id); }}
                              style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block', pointerEvents: 'none' }} />
                            <span className="cp-play-icon">▶</span>
                          </>
                        ) : (
                          <img
              src={url}
              alt=""
              loading="lazy"
                            onError={async e => {
                              const freshUrl = await refreshProfileMediaUrl(item.id);
                              if (!freshUrl) e.currentTarget.parentElement!.style.display = 'none';
                            }}
                          />
                        )}
                        {item.is_collab && (
                          <span className="cp-collab-media-badge">
                            Tagged as {item.collaborator_role || 'Collaborator'}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="cp-modal-no-media"><p>No {viewMediaType} yet.</p></div>
              )}
            </div>
          )}

          {connectMsg && <div className="cp-connect-msg">{connectMsg}</div>}
          <div className="cp-modal-actions">
            {!canInteract ? (
              <button onClick={() => onAuthRequired?.()} className="cp-modal-btn cp-modal-btn--filled">
                Login To Interact
              </button>
            ) : (
              <>
                {!profile.connectionStatus && (
                  <button onClick={handleConnect} className="cp-modal-btn cp-modal-btn--filled" disabled={connecting}>
                    {connecting ? 'Sending…' : 'Connect'}
                  </button>
                )}
                {profile.connectionStatus === 'pending' && <span className="cp-status-badge">Request Pending</span>}
                {profile.connectionStatus === 'accepted' && (
                  <>
                    <button onClick={() => { navigate(`/direct-messages/${userId}`); onClose(); }} className="cp-modal-btn cp-modal-btn--filled">
                      Chat
                    </button>
                    <button onClick={handleDisconnect} className="cp-modal-btn" disabled={disconnecting}>
                      {disconnecting ? 'Disconnecting...' : 'Disconnect'}
                    </button>
                  </>
                )}
                <button
                  onClick={handleFollowToggle}
                  className={`cp-modal-btn cp-modal-btn--follow${profile.isFollowing ? ' is-following' : ''}`}
                  disabled={followUpdating}
                >
                  {followUpdating ? 'Updating...' : profile.isFollowing ? 'Unfollow' : 'Follow'}
                </button>
              </>
            )}
            {false && profile?.connectionStatus === 'accepted' && (
              <button onClick={() => { navigate(`/direct-messages/${userId}`); onClose(); }} className="cp-modal-btn cp-modal-btn--filled">
                💬 Chat
              </button>
            )}
          </div>
        </div>
      </div>

      {lightbox && (
        <MediaInteractionModal
          mediaId={lightbox.mediaId}
          src={lightbox.url}
          isVideo={lightbox.isVideo}
          caption={([...(profile.photos || []), ...(profile.videos || [])].find((item) => item.id === lightbox.mediaId)?.caption) || null}
          canInteract={canInteract}
          onAuthRequired={onAuthRequired}
          shareUrl={getPublicPostShareUrl(lightbox.mediaId)}
          shareTitle={`${profile.user.name} on FilMini`}
          prefilledLikeCount={([...(profile.photos || []), ...(profile.videos || [])].find((item) => item.id === lightbox.mediaId)?.like_count) || 0}
          prefilledCommentCount={([...(profile.photos || []), ...(profile.videos || [])].find((item) => item.id === lightbox.mediaId)?.comment_count) || 0}
          collaborators={(collabMap[lightbox.mediaId] || []).map(c => ({
            ...c,
            user_dp: getMediaUrl(c.user_dp),
          }))}
          collaboratorsLoading={Boolean(collabLoading[lightbox.mediaId])}
          onRefreshSrc={() => refreshProfileMediaUrl(lightbox.mediaId)}
          onClose={() => {
            setLightbox(null);
            if (initialMediaId) {
              onClose();
            }
          }}
        />
      )}
    </>
  );
};

export default ContentPage;
